import { useEffect, useRef, useState } from 'react';
import { Check, Copy, X } from 'react-feather';

import { Button } from '~ui/base/button';
import { Typography } from '~ui/base/typography';
import { cn } from '~lib/utils/style';
import { formatBytes, formatDateTime } from '~lib/utils/format-bytes';
import { useTemporaryLink } from '~lib/storage/use-temporary-link';
import type { DatasetFile } from '~lib/storage/datasets';

interface TemporaryLinkDialogProps {
  datasetName: string;
  zipFile: DatasetFile;
  onClose: () => void;
}

const HOUR_MS = 60 * 60 * 1000;

// Offered validities. The last is Azure's ceiling for a user delegation key;
// createTemporaryLink trims it by a clock-skew margin.
const DURATIONS = [
  { label: '1 hour', ms: HOUR_MS },
  { label: '8 hours', ms: 8 * HOUR_MS },
  { label: '24 hours', ms: 24 * HOUR_MS },
  { label: '7 days', ms: 7 * 24 * HOUR_MS },
] as const;

const COPIED_RESET_MS = 2000;

function TemporaryLinkDialog({
  datasetName,
  zipFile,
  onClose,
}: TemporaryLinkDialogProps) {
  const [durationMs, setDurationMs] = useState<number>(DURATIONS[0].ms);
  const [copied, setCopied] = useState(false);
  const { state, create } = useTemporaryLink();
  const cardRef = useRef<HTMLDivElement>(null);

  // Mint as soon as the dialog opens, and again whenever the validity
  // changes. Minting has no side effects on the Azure side (the key is
  // derived, not stored), so re-running it is cheap. The common path is
  // therefore: open, copy, close.
  useEffect(() => {
    setCopied(false);
    create(zipFile.fullPath, durationMs);
  }, [create, zipFile.fullPath, durationMs]);

  // Move focus into the dialog and let Escape close it.
  useEffect(() => {
    cardRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const ready = state.status === 'ready';

  const copyLink = async () => {
    if (state.status !== 'ready') return;
    try {
      await navigator.clipboard.writeText(state.link.url);
      setCopied(true);
    } catch {
      // Clipboard access denied (insecure context or a strict browser
      // policy). The URL stays selectable in the field for a manual copy.
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="temporary-link-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 px-4 backdrop-blur-sm"
    >
      <div
        ref={cardRef}
        tabIndex={-1}
        className="flex w-full max-w-xl flex-col overflow-hidden rounded-[1.25rem] bg-card shadow-md focus:outline-none"
      >
        <header className="flex items-start justify-between gap-x-6 px-8 pt-8">
          <div>
            <Typography variant="headline-md" as="h2" id="temporary-link-title">
              Temporary link
            </Typography>
            <Typography variant="body-md" as="p" className="mt-2 text-muted-foreground">
              A URL that downloads <span className="font-mono">{datasetName}.zip</span> (
              {formatBytes(zipFile.size)}) without signing in. Hand it to a CI job, a
              notebook, curl or azcopy. It is signed with your account, so it stops
              working when it expires or if your own access is removed.
            </Typography>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={cn(
              'inline-flex size-8 shrink-0 items-center justify-center rounded-full',
              'text-muted-foreground transition-colors hover:bg-card-highlight hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
            )}
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="flex flex-col gap-y-6 px-8 py-6">
          <div className="flex flex-col gap-y-3">
            <span className="text-xs font-medium tracking-wide text-muted-foreground">
              VALID FOR
            </span>
            <div
              role="group"
              aria-label="Link validity"
              className="flex flex-wrap gap-x-2 gap-y-2"
            >
              {DURATIONS.map((duration) => {
                const selected = duration.ms === durationMs;
                return (
                  <Button
                    key={duration.ms}
                    variant={selected ? 'secondary' : 'tertiary'}
                    size="sm"
                    aria-pressed={selected}
                    onClick={() => setDurationMs(duration.ms)}
                  >
                    {duration.label}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <label
              htmlFor="temporary-link-url"
              className="text-xs font-medium tracking-wide text-muted-foreground"
            >
              LINK
            </label>
            <input
              id="temporary-link-url"
              type="text"
              readOnly
              value={ready ? state.link.url : ''}
              placeholder={state.status === 'creating' ? 'Creating link…' : ''}
              onFocus={(event) => event.currentTarget.select()}
              spellCheck={false}
              className={cn(
                'w-full rounded-full border border-border bg-input px-4 py-2.5',
                'font-mono text-sm text-foreground placeholder:text-muted-foreground',
                'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-4 focus:ring-offset-card',
              )}
            />
            {ready && (
              <Typography variant="caption-md" as="p" className="text-muted-foreground">
                Valid until {formatDateTime(state.link.expiresAt)}. Treat it like a
                password: anyone who has it can download the archive until then.
              </Typography>
            )}
            {state.status === 'error' && (
              <div className="mt-1 rounded-[1rem] border border-destructive/30 bg-destructive-container px-4 py-3 text-destructive-container-foreground">
                <Typography variant="body-md" as="p">
                  Couldn&apos;t create the link. {state.error.message}
                </Typography>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  onClick={() => create(zipFile.fullPath, durationMs)}
                >
                  Try again
                </Button>
              </div>
            )}
          </div>
        </div>

        <footer className="flex items-center justify-end gap-x-3 border-t border-border bg-background/40 px-8 py-5">
          <Button variant="tertiary" size="md" onClick={onClose}>
            Close
          </Button>
          <Button size="md" onClick={copyLink} disabled={!ready}>
            {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

TemporaryLinkDialog.displayName = 'TemporaryLinkDialog';

export { TemporaryLinkDialog };
